import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  meGQL,
  getUserNotificationsGQL,
  clearNotificationGQL,
} from "../../graphql";
import type { Query, UserNotification } from "@onmo/graphql/graphql";
import AppBar from "../../components/common/AppBar/AppBar";
import { useTranslation } from "react-i18next";
import { useStyles } from "./Notification.style";
import { NotificationItem } from "../../components/common/NotificationItem";
import { Typography } from "../../components/core/Typography/Typography";
import { Icon } from "../../components/core/Icon/Icon";
import { TypographyVariant } from "../../constants/typography";
import Button from "../../components/core/Button/Button";
import { NotificationMd } from "../../models/notification";

function formatHeading(d: Date) {
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return "Today";
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function NotificationsPage() {
  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();

  const { data: meData } = useQuery(meGQL);
  const userId = meData?.me?.id;

  const { data, refetch: refetchUserNotifications } = useQuery<Query>(
    getUserNotificationsGQL,
    {
      variables: { userId, product: "tgp" },
      skip: !userId,
      fetchPolicy: "cache-and-network",
    },
  );

  const notifications = NotificationMd.filterNotificationType(
    data?.user?.notifications,
  );

  // group by day
  const groups = useMemo(() => {
    const map = new Map<
      string,
      { heading: string; items: UserNotification[] }
    >();
    notifications.forEach((n) => {
      const dt = new Date(n.createdAt);
      const key = `${dt.getFullYear()}-${dt.getMonth() + 1}-${dt.getDate()}`;
      if (!map.has(key)) {
        map.set(key, { heading: formatHeading(dt), items: [] });
      }
      map.get(key)?.items.push(n);
    });
    return Array.from(map.values()).sort((a, b) => {
      const ad = new Date(a.items[0].createdAt);
      const bd = new Date(b.items[0].createdAt);
      return bd.getTime() - ad.getTime();
    });
  }, [notifications]);

  const [clearNotifications] = useMutation(clearNotificationGQL);

  // remove one notification
  const handleDismiss = async (id: string) => {
    await clearNotifications({ variables: { notificationIds: [id] } });
    refetchUserNotifications();
  };

  // clear all
  const handleClearAll = async () => {
    await clearNotifications();
    refetchUserNotifications();
  };

  return (
    <div className={classes.page}>
      <AppBar
        leftItems={
          <Icon
            name="icon-bold-arrow-left-3"
            onClick={() => navigate(-1)}
            data-testid="back-icon"
          />
        }
        centerItems={
          <Typography variant={TypographyVariant.BodyB4Medium}>
            {t("common_Notification_other")}
          </Typography>
        }
      />
      <div className={classes.root}>
        {groups.map((g) => (
          <div key={g.heading} className={classes.group}>
            <div className={classes.groupHeading}>{g.heading}</div>

            {g.items.map((item: UserNotification, idx: number) => (
              <NotificationItem
                notification={item}
                key={item.id + idx}
                // fromFullPage={true}
                className={classes.noti}
                onClose={() => handleDismiss(item.id)}
              />
            ))}
          </div>
        ))}
      </div>

      {notifications.length === 0 && (
        <div className={classes.empty}>{t("common_No notifications yet.")}</div>
      )}

      {notifications.length > 0 && (
        <div className={classes.buttonGroup}>
          <Button variant="contained" fullWidth onClick={handleClearAll}>
            {t("common_Clear All")}
          </Button>
        </div>
      )}
    </div>
  );
}
